import React from 'react';
import { useMessages, usePaths } from "react-chatbotify";
import { setAgentPayload } from '../agentClient';
import { agentTextToMarkdown } from '../textUtils';
import { AgentReply, AgentSend } from '../types';

// Basic markdown -> HTML for Adaptive Card TextBlocks (bold/italic/links), set once
// globally on the Adaptive Cards SDK.
let agentCardMarkdownConfigured = false;
const configureAgentCardMarkdown = (AC: any) => {
  if (agentCardMarkdownConfigured || !AC?.AdaptiveCard) return;
  AC.AdaptiveCard.onProcessMarkdown = (text: string, result: any) => {
    result.outputHtml = text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    result.didProcess = true;
  };
  agentCardMarkdownConfigured = true;
};

// PowerApps / Power Platform form links are embedded inline (iframe) instead of
// being opened in a new tab.
const isEmbeddableFormUrl = (url: string): boolean =>
  /powerapps\.com|powerappsportals|powerpages|\/play\/e\//i.test(url);

// A long choice list is hard to scan, so any ChoiceSet with more than this many
// options is collapsed into a dropdown.
const CHOICESET_DROPDOWN_THRESHOLD = 8;

// Recursively rewrites large single-select Input.ChoiceSet elements into a compact
// native dropdown. Multi-select sets are left as checkboxes here (the SDK can't
// render a native multi-select dropdown) and are collapsed into a custom dropdown
// panel after render — see collapseMultiSelectChoiceSets. Mutates a deep clone so
// the original card object is left untouched.
const toDropdownChoiceSets = (card: any): any => {
  const visit = (node: any): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!node || typeof node !== 'object') return;

    if (node.type === 'Input.ChoiceSet' && Array.isArray(node.choices) && node.choices.length > CHOICESET_DROPDOWN_THRESHOLD && !node.isMultiSelect) {
      node.style = 'compact';
      if (!node.placeholder) node.placeholder = 'Select an option';
    }

    Object.values(node).forEach(visit);
  };

  const clone = typeof structuredClone === 'function'
    ? structuredClone(card)
    : JSON.parse(JSON.stringify(card));
  visit(clone);
  return clone;
};

// Collapses each large multi-select ChoiceSet (rendered by the SDK as a flat list
// of checkboxes) into a searchable dropdown panel. The original checkbox inputs are
// preserved inside the panel so the card's value collection / submit still works.
// Returns a disposer that removes the outside-click listeners it registers.
const collapseMultiSelectChoiceSets = (root: HTMLElement): (() => void) => {
  const disposers: Array<() => void> = [];
  const lists = Array.from(root.querySelectorAll<HTMLElement>('.ac-choiceSetInput-multiSelect'));

  lists.forEach(list => {
    const checkboxes = Array.from(list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    if (checkboxes.length <= CHOICESET_DROPDOWN_THRESHOLD || list.dataset.rcbDropdown === '1') return;
    list.dataset.rcbDropdown = '1';

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:relative;width:100%;';
    list.parentNode?.insertBefore(wrapper, list);

    const control = document.createElement('button');
    control.type = 'button';
    control.style.cssText = 'width:100%;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 10px;border:1px solid #cfcfcf;border-radius:6px;background:#fff;font:inherit;font-size:14px;cursor:pointer;text-align:left;';
    const summary = document.createElement('span');
    summary.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    const caret = document.createElement('span');
    caret.textContent = '\u25be';
    caret.style.flex = '0 0 auto';
    control.appendChild(summary);
    control.appendChild(caret);

    const panel = document.createElement('div');
    panel.style.cssText = 'position:absolute;top:calc(100% + 4px);left:0;right:0;z-index:20;background:#fff;border:1px solid #cfcfcf;border-radius:6px;box-shadow:0 4px 14px rgba(0,0,0,0.14);padding:6px;display:none;';

    const filter = document.createElement('input');
    filter.type = 'text';
    filter.placeholder = 'Search...';
    filter.style.cssText = 'width:100%;box-sizing:border-box;margin-bottom:6px;padding:6px 8px;border:1px solid #e0e0e0;border-radius:5px;font:inherit;font-size:13px;outline:none;';

    const scroll = document.createElement('div');
    scroll.style.cssText = 'max-height:240px;overflow:auto;';
    list.style.width = '100%';
    scroll.appendChild(list);

    panel.appendChild(filter);
    panel.appendChild(scroll);
    wrapper.appendChild(control);
    wrapper.appendChild(panel);

    const rows = Array.from(list.children) as HTMLElement[];

    const updateSummary = () => {
      const selected = checkboxes.filter(c => c.checked);
      summary.textContent = selected.length === 0
        ? 'Select endpoints'
        : selected.length === 1
          ? (selected[0].getAttribute('aria-label') || '1 selected')
          : `${selected.length} selected`;
      summary.style.color = selected.length ? '#1A1A1A' : '#767676';
    };
    updateSummary();
    checkboxes.forEach(c => c.addEventListener('change', updateSummary));

    filter.addEventListener('input', () => {
      const q = filter.value.trim().toLowerCase();
      rows.forEach(row => {
        row.style.display = (row.textContent || '').toLowerCase().includes(q) ? '' : 'none';
      });
    });

    const stop = (e: Event) => e.stopPropagation();
    panel.addEventListener('click', stop);
    control.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = panel.style.display !== 'none';
      panel.style.display = isOpen ? 'none' : 'block';
      if (!isOpen) filter.focus();
    });
    const onOutside = () => { panel.style.display = 'none'; };
    document.addEventListener('click', onOutside);
    disposers.push(() => document.removeEventListener('click', onOutside));
  });

  return () => disposers.forEach(fn => fn());
};

// Appends an embedded PowerApps form (iframe) inside an already-rendered card,
// without injecting a new chat message so the surrounding text and option buttons
// (Yes/No/Exit) stay in place.
const embedFormIntoCard = (container: HTMLElement, url: string, title?: string) => {
  if (container.querySelector('iframe.rcb-embedded-form')) return; // already embedded
  const src = /[?&]source=/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}source=iframe`;
  const wrap = document.createElement('div');
  wrap.style.marginTop = '12px';

  const iframe = document.createElement('iframe');
  iframe.className = 'rcb-embedded-form';
  iframe.src = src;
  iframe.title = title ?? 'Embedded form';
  iframe.style.cssText = 'width:100%;height:600px;border:1px solid #ececec;border-radius:8px;background:#fff';
  iframe.setAttribute('allow', 'clipboard-read; clipboard-write; camera; microphone; geolocation');

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'Having trouble? Open the form in a new tab';
  link.style.cssText = 'display:inline-block;margin-top:8px;font-size:12px;color:#1565c0';

  wrap.appendChild(iframe);
  wrap.appendChild(link);
  container.appendChild(wrap);
};

// Imperatively renders a single Copilot Studio Adaptive Card into a sub-container.
// OpenUrl actions open a new tab (PowerApps forms embed inline); Submit/Execute
// actions are handed back through `onAction` so the parent decides what to do next.
// Once `locked`, the already-rendered inputs are disabled in place (values kept,
// action buttons hidden) so a completed step reads as filled and non-editable.
const AdaptiveCardBlock = ({
  card,
  index,
  locked,
  onAction,
}: {
  card: any;
  index: number;
  locked: boolean;
  onAction: (index: number, data: any, title: string) => void;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  // Keep the latest action handler / position in refs so the render effect can
  // depend on `card` alone — re-parsing on any prop-identity change would wipe the
  // values the user has already typed into the form.
  const onActionRef = React.useRef(onAction);
  const indexRef = React.useRef(index);
  onActionRef.current = onAction;
  indexRef.current = index;

  React.useEffect(() => {
    let cancelled = false;
    let disposeDropdowns: (() => void) | undefined;
    (async () => {
      try {
        const mod: any = await import('adaptivecards');
        // adaptivecards is CommonJS; the classes may sit on the namespace or default.
        const AC: any = mod?.AdaptiveCard ? mod : (mod?.default ?? mod);
        if (cancelled || !ref.current || !AC?.AdaptiveCard) return;
        configureAgentCardMarkdown(AC);
        const adaptiveCard = new AC.AdaptiveCard();
        adaptiveCard.onExecuteAction = (action: any) => {
          const typeName = action?.getJsonTypeName?.() ?? '';
          if (typeName === 'Action.OpenUrl' && action?.url) {
            // Embed PowerApps forms inline within this same card bubble (so the
            // agent's follow-up text and Yes/No buttons stay visible); other links
            // open in a new tab.
            if (isEmbeddableFormUrl(action.url)) {
              if (ref.current) embedFormIntoCard(ref.current, action.url, action?.title);
            } else {
              window.open(action.url, '_blank', 'noopener,noreferrer');
            }
            return;
          }
          const data = action?.data;
          const title = typeof action?.title === 'string' ? action.title : '';
          // Submit/Execute: send the action's data in the activity `value` so the
          // agent handles it as a card action (needed for the consent card);
          // title-only buttons fall back to sending their text.
          if (data == null && !title) return;
          onActionRef.current(indexRef.current, data, title);
        };
        adaptiveCard.parse(toDropdownChoiceSets(card));
        const el = adaptiveCard.render();
        ref.current.innerHTML = '';
        if (el) ref.current.appendChild(el);
        if (ref.current) disposeDropdowns = collapseMultiSelectChoiceSets(ref.current);
      } catch (e) {
        console.error('Failed to render adaptive card:', e);
        if (ref.current) ref.current.textContent = 'Unable to display this card.';
      }
    })();
    return () => { cancelled = true; disposeDropdowns?.(); };
  }, [card]);

  // Lock a completed step in place — never re-parse the card (that would wipe the
  // entered values); just disable the live inputs and hide the action buttons.
  React.useEffect(() => {
    const root = ref.current;
    if (!locked || !root) return;
    root.querySelectorAll<HTMLInputElement>('input, select, textarea, button')
      .forEach(el => { el.disabled = true; });
    root.querySelectorAll<HTMLElement>('.ac-actionSet').forEach(el => { el.style.display = 'none'; });
    root.style.opacity = '0.85';
    root.style.pointerEvents = 'none';
  }, [locked]);

  return <div ref={ref} style={{ marginTop: index ? 12 : 0 }} />;
};

// Renders a Copilot Studio Adaptive Card inside a chat bubble. When `onCardAction`
// is supplied, Submit/Execute actions (e.g. a multi-step form's "Next") are sent to
// the agent and any follow-up *cards* continue in this same bubble, while completed
// steps are locked (filled + non-editable). Text replies (e.g. the client-token
// prompt) are injected as their own chat messages. Without `onCardAction`, actions
// fall back to routing through the `agentAction` flow path.
export const AdaptiveCardMessage = ({
  card,
  onCardAction,
}: {
  card: any;
  onCardAction?: (payload: AgentSend) => Promise<AgentReply>;
}) => {
  const { injectMessage } = useMessages();
  const { goToPath } = usePaths();
  const [cards, setCards] = React.useState<any[]>([card]);
  const [lockedThrough, setLockedThrough] = React.useState(-1);
  const [busy, setBusy] = React.useState(false);

  const handleAction = React.useCallback(async (index: number, data: any, title: string) => {
    if (!onCardAction) {
      // Fallback: route the action back through the agentAction flow path.
      setAgentPayload(data != null ? { value: data, text: title } : title);
      await injectMessage(title || 'Selected', "USER");
      await goToPath("agentAction");
      return;
    }
    // Lock the submitted step (and everything before it) so it reads as completed.
    setLockedThrough(prev => Math.max(prev, index));
    const payload: AgentSend = data != null ? { value: data, text: title } : title;
    setBusy(true);
    try {
      const reply = await onCardAction(payload);
      const nextCards: any[] = [];
      for (const it of reply.items) {
        // Follow-up cards continue inside this same bubble…
        if (it.kind === 'card') nextCards.push(it.card);
        // …while any text (e.g. the client-token prompt) becomes its own message.
        else if (it.text.trim()) await injectMessage(agentTextToMarkdown(it.text), "BOT");
      }
      if (nextCards.length) setCards(prev => [...prev, ...nextCards]);
    } catch (e) {
      console.error('Adaptive card action failed:', e);
      await injectMessage("Sorry, I couldn't continue just now. Please try again.", "BOT");
    } finally {
      setBusy(false);
    }
  }, [onCardAction, injectMessage, goToPath]);

  return (
    <div
      className="rcb-adaptive-card"
      style={{
        display: 'inline-block',
        width: 'fit-content',
        maxWidth: '85%',
        marginLeft: '17px',
        marginTop: '10px',
        background: '#F5F5F5',
        border: '1px solid #ececec',
        borderRadius: '14px 14px 14px 2px',
        padding: '12px 14px',
        boxSizing: 'border-box',
      }}
    >
      {cards.map((c, i) => (
        <AdaptiveCardBlock
          key={i}
          card={c}
          index={i}
          locked={i <= lockedThrough}
          onAction={handleAction}
        />
      ))}
      {busy && <div className="rcb-agent-card-text" style={{ marginTop: 12, opacity: 0.7 }}>Working…</div>}
    </div>
  );
};
